import { create } from 'zustand'

/*
Navigation-stack–driven mobile topbar context.

Screens declare their *own* title (and optional deep-link fallback target)
via `useTopbarContext`. The hook pushes/pops stack entries based on
`useNavigationType()` from react-router, so the stack mirrors browser
history:

  PUSH    → append entry (or update title if same path, handles strict-mode
            double-fire and late title resolution)
  POP     → truncate stack to the entry whose path matches `location` (this
            is what browser-back / forward nav produces)
  REPLACE → overwrite top of stack (for `navigate(path, {replace: true})`)

The topbar shows the current screen's title and a back arrow when there's
somewhere to go back to — either the previous stack entry, or (if the user
deep-linked straight into a detail screen) the current entry's declared
`fallbackBackTo` so they aren't stranded. The back button never shows a
text label — just an arrow.
*/

export type TopbarStackEntryT = {
  path: string
  title: string
  fallbackBackTo: string | null
}

type TopbarContextStoreT = {
  stack: TopbarStackEntryT[]
  push: (entry: TopbarStackEntryT) => void
  replace: (entry: TopbarStackEntryT) => void
  popTo: (path: string) => void
  reset: () => void
}

// Find the last index in the stack matching `path`. Manual reverse loop to
// avoid relying on Array.prototype.findLastIndex (ES2023) target.
function lastIndexOfPath(stack: TopbarStackEntryT[], path: string): number {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].path === path) return i
  }
  return -1
}

export const useTopbarContextStore = create<TopbarContextStoreT>(set => ({
  stack: [],

  push: entry =>
    set(state => {
      const top = state.stack[state.stack.length - 1]
      // If PUSH arrives for the same path as the current top (strict-mode
      // double-fire, or title resolving after data fetch), update the top
      // in place instead of duplicating it.
      if (top && top.path === entry.path) {
        const next = state.stack.slice(0, -1)
        next.push(entry)
        return { stack: next }
      }
      return { stack: [...state.stack, entry] }
    }),

  replace: entry =>
    set(state => {
      if (state.stack.length === 0) return { stack: [entry] }
      const next = state.stack.slice(0, -1)
      next.push(entry)
      return { stack: next }
    }),

  popTo: path =>
    set(state => {
      const idx = lastIndexOfPath(state.stack, path)
      // Unknown path (deep link / hard refresh while a stack is lingering)
      // → start fresh. The caller will immediately push/replace the real
      // entry with its title.
      if (idx === -1) return { stack: [] }
      return { stack: state.stack.slice(0, idx + 1) }
    }),

  reset: () => set({ stack: [] }),
}))
